import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel'
import { LitElement, html, css } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'

const EMBLA_INIT = 'embla-init'
const EMBLA_SELECT = 'embla-select'

// MARK: Provider

@customElement('mitene-carousel')
export class MiteneCarousel extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
  `

  @property({ type: Boolean, reflect: true })
  loop = false

  @property({ type: String, reflect: true })
  align: 'start' | 'center' | 'end' = 'start'

  @property({ type: String, reflect: true })
  axis: 'x' | 'y' = 'x'

  @property({ type: String, reflect: true })
  direction: 'ltr' | 'rtl' = 'ltr'

  @property({ type: Boolean, reflect: true, attribute: 'drag-free' })
  dragFree = false

  @property({ type: Boolean, reflect: true, attribute: 'skip-snaps' })
  skipSnaps = true

  @property({ type: Number, reflect: true })
  duration = 25

  @property({ type: Number, reflect: true, attribute: 'start-index' })
  startIndex = 0

  @property({ type: String, reflect: true, attribute: 'slides-to-scroll' })
  slidesToScroll: 'auto' | `${number}` = '1'

  @property({ type: String, reflect: true, attribute: 'contain-scroll' })
  containScroll: 'trimSnaps' | 'keepSnaps' | 'false' = 'trimSnaps'

  @property({ type: Boolean, reflect: true, attribute: 'watch-drag' })
  watchDrag = true

  private _embla: EmblaCarouselType | undefined
  private _viewportData: {
    viewport: HTMLElement
    container: HTMLElement
    slides: HTMLElement[]
  } | null = null

  get embla() {
    return this._embla
  }

  protected render() {
    return html`<slot></slot>`
  }

  registerViewport(viewport: HTMLElement, container: HTMLElement, slides: HTMLElement[]) {
    this._viewportData = { viewport, container, slides }
    this.initEmbla()
  }

  private initEmbla() {
    if (!this._viewportData) {
return
}
    this._embla?.destroy()

    const { viewport, container, slides } = this._viewportData
    const slidesToScroll =
      this.slidesToScroll === 'auto' ? 'auto' : Number(this.slidesToScroll) || 1

    this._embla = EmblaCarousel(viewport, {
      loop: this.loop,
      align: this.align,
      axis: this.axis,
      direction: this.direction,
      dragFree: this.dragFree,
      skipSnaps: this.skipSnaps,
      duration: this.duration,
      startIndex: this.startIndex,
      slidesToScroll,
      containScroll: this.containScroll === 'false' ? false : this.containScroll,
      watchDrag: this.watchDrag,
      container,
      slides,
    })

    this._embla.on('select', () => this.dispatchEvent(new Event(EMBLA_SELECT)))
    this.dispatchEvent(new Event(EMBLA_INIT))
  }

  protected updated(changed: Map<string, unknown>) {
    const optionKeys = [
      'loop',
      'align',
      'axis',
      'direction',
      'dragFree',
      'skipSnaps',
      'duration',
      'startIndex',
      'slidesToScroll',
      'containScroll',
      'watchDrag',
    ]
    if (optionKeys.some(k => changed.has(k)) && this._viewportData) {
      this.initEmbla()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._embla?.destroy()
    this._embla = undefined
  }

  goTo(index: number) {
    this._embla?.scrollTo(index)
  }
  scrollPrev() {
    this._embla?.scrollPrev()
  }
  scrollNext() {
    this._embla?.scrollNext()
  }
}

// MARK: Viewport

@customElement('mitene-carousel-viewport')
export class MiteneCarouselViewport extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .viewport {
      overflow: hidden;
      width: 100%;
    }

    .container {
      display: flex;
      gap: var(--carousel-slide-gap, 0px);
      touch-action: pan-y pinch-zoom;
    }

    ::slotted(*) {
      flex: 0 0 var(--carousel-slide-size, 100%);
      min-width: 0;
      box-sizing: border-box;
    }
  `

  @query('.viewport')
  private viewportEl!: HTMLElement

  @query('.container')
  private containerEl!: HTMLElement

  private get carousel() {
    return this.closest('mitene-carousel') as MiteneCarousel | null
  }

  private get slideElements(): HTMLElement[] {
    const slot = this.renderRoot.querySelector('slot') as HTMLSlotElement | null
    return slot ? (slot.assignedElements() as HTMLElement[]) : []
  }

  protected render() {
    return html`
      <div class="viewport" part="viewport">
        <div class="container" part="track">
          <slot @slotchange=${this.onSlotChange}></slot>
        </div>
      </div>
    `
  }

  private register() {
    this.carousel?.registerViewport(this.viewportEl, this.containerEl, this.slideElements)
  }

  private onSlotChange() {
    this.register()
  }

  protected firstUpdated() {
    this.register()
  }
}

// MARK: Consumers
class MiteneCarouselChild extends LitElement {
  protected get carousel(): MiteneCarousel | null {
    return this.closest('mitene-carousel') as MiteneCarousel | null
  }

  protected syncState(): void {}

  private handleSync = () => this.syncState()

  connectedCallback() {
    super.connectedCallback()
    const carousel = this.carousel
    if (!carousel) {
return
}
    carousel.addEventListener(EMBLA_INIT, this.handleSync)
    carousel.addEventListener(EMBLA_SELECT, this.handleSync)
    if (carousel.embla) {
this.syncState()
}
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    const carousel = this.carousel
    if (!carousel) {
return
}
    carousel.removeEventListener(EMBLA_INIT, this.handleSync)
    carousel.removeEventListener(EMBLA_SELECT, this.handleSync)
  }
}

// MARK: Dots
@customElement('mitene-carousel-dots')
export class MiteneCarouselDots extends MiteneCarouselChild {
  static styles = css`
    :host {
      display: block;
    }

    .dots {
      display: flex;
      justify-content: center;
      gap: var(--carousel-dot-gap, 8px);
    }

    .dot {
      width: var(--carousel-dot-size, 10px);
      height: var(--carousel-dot-size, 10px);
      border-radius: 50%;
      background: var(--carousel-dot-bg, transparent);
      border: var(--carousel-dot-border, 1px solid #333);
      padding: 0;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .dot[aria-selected='true'] {
      background: var(--carousel-dot-active-bg, #333);
    }
  `

  @state() private activeIndex = 0
  @state() private scrollSnaps: number[] = []

  protected syncState() {
    const embla = this.carousel?.embla
    if (!embla) {
return
}
    this.scrollSnaps = embla.scrollSnapList()
    this.activeIndex = embla.selectedScrollSnap()
  }

  protected render() {
    if (this.scrollSnaps.length === 0) {
return null
}
    return html`
      <div class="dots" part="dots" role="tablist">
        ${this.scrollSnaps.map(
          (_, i) => html`
            <button
              class="dot"
              role="tab"
              aria-label=${`Slide ${i + 1}`}
              aria-selected=${i === this.activeIndex ? 'true' : 'false'}
              @click=${() => this.carousel?.goTo(i)}
            ></button>
          `,
        )}
      </div>
    `
  }
}

// MARK: Prev Button
@customElement('mitene-carousel-prev')
export class MiteneCarouselPrev extends MiteneCarouselChild {
  static styles = css`
    :host {
      display: inline-block;
    }

    button {
      all: unset;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    button:disabled {
      opacity: 0.3;
      cursor: default;
    }
  `

  @state() private disabled = false

  protected syncState() {
    const embla = this.carousel?.embla
    if (!embla) {
return
}
    this.disabled = !embla.canScrollPrev()
  }

  protected render() {
    return html`
      <button
        part="button"
        aria-label="Previous slide"
        ?disabled=${this.disabled}
        @click=${() => this.carousel?.scrollPrev()}
      >
        <slot>&#8249;</slot>
      </button>
    `
  }
}

// MARK: Next Button
@customElement('mitene-carousel-next')
export class MiteneCarouselNext extends MiteneCarouselChild {
  static styles = css`
    :host {
      display: inline-block;
    }

    button {
      all: unset;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    button:disabled {
      opacity: 0.3;
      cursor: default;
    }
  `

  @state() private disabled = false

  protected syncState() {
    const embla = this.carousel?.embla
    if (!embla) {
return
}
    this.disabled = !embla.canScrollNext()
  }

  protected render() {
    return html`
      <button
        part="button"
        aria-label="Next slide"
        ?disabled=${this.disabled}
        @click=${() => this.carousel?.scrollNext()}
      >
        <slot>&#8250;</slot>
      </button>
    `
  }
}
